import {
  TextField,
  Box,
  Typography,
  Grid,
  Collapse,
  Alert,
  Autocomplete as MuiAutocomplete,
  Divider,
  Stack,
} from '@mui/material'
import { ExpandMore } from '@mui/icons-material'
import shallow from 'zustand/shallow'
import {
  Controller,
  useWatch,
  Control,
  UseFormTrigger,
  UseFormGetValues,
  UseFormResetField,
  UseFormSetValue,
  useFieldArray,
} from 'react-hook-form'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { SearchLocation } from 'types'
import { useCreatePropertyAdStore } from 'store'
import { FormValues } from '..'

import useDebounce from 'hooks/useDebounce'
import MapComponent from './Map'
import SearchLocationService from 'api/services/SearchLocation'

interface Props {
  control: Control<FormValues>
  trigger: UseFormTrigger<FormValues>
  getValues: UseFormGetValues<FormValues>
  resetField: UseFormResetField<FormValues>
  setValue: UseFormSetValue<FormValues>
}

const StepPropertyLocation = (props: Props) => {
  const { control, trigger, getValues, setValue } = props

  const { t } = useTranslation()

  const [uncompleteStep, invalidateStep] = useCreatePropertyAdStore(
    (state) => [state.uncompleteStep, state.invalidateStep],
    shallow
  )

  const firstRender = React.useRef<unknown>(true)
  React.useEffect(() => {
    if (firstRender) firstRender.current = false
  }, [])

  const watch = useWatch({ control, name: 'location' })

  const checkValidStatus = async () => {
    if (!firstRender.current) {
      const isValid = await trigger(['location.addresses'])
      if (!isValid) {
        uncompleteStep('location')
        invalidateStep('location')
      }
    }
  }

  const debouncedCheck = useDebounce(checkValidStatus, 400)
  const debouncedSetTyping = useDebounce(() => setIsTyping(false), 400)

  React.useEffect(() => {
    debouncedCheck()
  }, [watch, debouncedCheck])

  const { fields } = useFieldArray({
    control,
    name: 'location.addresses',
    keyName: 'formId',
  })

  const [localIsError, setLocalIsError] = React.useState(false)

  const handleCloseError = () => {
    setLocalIsError(false)
  }

  const [showAddressFields, setShowAddressFields] = React.useState(false)

  const [options, setOptions] = React.useState<SearchLocation[]>([])
  const [vvalue, setVValue] = React.useState<SearchLocation | null>(null)
  const [inputValue, setInputValue] = React.useState('')
  const searchLocations =
    SearchLocationService.hooks.useSearchLocationByCriteria({
      onMutate: () => {
        setLocalIsError(false)
      },
      onSuccess: (data) => {
        if (data && data?.length) {
          setOptions(data)
        } else {
          setOptions([])
        }
      },
      onError: (err) => {
        setLocalIsError(true)
      },
    })
  const locale = 'el'
  const debouncedHandleSearch = useDebounce(
    (value) => searchLocations.mutate({ locale, locationPath: value }),
    600
  )

  const [isTyping, setIsTyping] = React.useState(false)
  React.useEffect(() => {
    let active = true

    if (inputValue === '' || inputValue?.length < 3) {
      setOptions(vvalue ? [vvalue] : [])
      return undefined
    }

    if (active) {
      debouncedHandleSearch(inputValue)
      debouncedSetTyping()
    }

    return () => {
      active = false
    }
  }, [vvalue, inputValue, debouncedHandleSearch, debouncedSetTyping])

  const handleInputChange = (e, newInputValue, reason) => {
    if (options?.length) {
      setOptions([])
    }
    setInputValue(newInputValue)
    setIsTyping(true)
    if (newInputValue === '' || newInputValue?.length < 3) {
      setIsTyping(false)
    }
  }

  return (
    <>
      <Box display="grid" gap={1} sx={{ paddingBottom: 4 }}>
        <Typography
          component="span"
          display="inline-block"
          color="error"
          sx={{ marginBottom: 4 }}
        >
          {`(*) ${t('manageEstateAd.steps.estateDescription.requiredFields')}`}
        </Typography>
        <Box>
          <Typography align="center" sx={{ my: 2 }}>
            {t('manageEstateAd.steps.estateLocation.chooseLocation')}
            {` `}
            <Typography component="span" display="inline-block" color="error">
              {` (*)`}
            </Typography>
          </Typography>
          <Controller
            control={control}
            name="location.searchLocation"
            rules={{
              required: t(`manageEstateAd.fieldRequired`) as unknown as string,
            }}
            render={({ field, fieldState: { error } }) => (
              <MuiAutocomplete
                {...field}
                onChange={(event: any, newValue: SearchLocation | null) => {
                  field.onChange(newValue)
                  setVValue(newValue)
                }}
                options={options}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option
                  else if (option?.i18nLocationName?.length) {
                    return option?.i18nLocationName[0]?.locationPath ?? ''
                  }
                  return ''
                }}
                filterOptions={(x) => x}
                loading={searchLocations.isLoading}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={Boolean(error)}
                    label={t(
                      'manageEstateAd.steps.estateLocation.searchPlaceholder'
                    )}
                    helperText={error ? error?.message : ' '}
                  />
                )}
                loadingText={
                  <Typography>
                    {t('manageEstateAd.steps.estateLocation.searching')}
                  </Typography>
                }
                noOptionsText={
                  <Typography>
                    {(inputValue || searchLocations.data?.length) &&
                    !isTyping &&
                    !searchLocations.isLoading
                      ? t('manageEstateAd.steps.estateLocation.noResults')
                      : t('manageEstateAd.steps.estateLocation.helperText')}
                  </Typography>
                }
              />
            )}
          />
          <Collapse in={localIsError}>
            <Alert severity="error" onClose={handleCloseError}>
              {t('error.unspecific')}
            </Alert>
          </Collapse>
        </Box>

        <Divider sx={{ mt: 4 }} />

        <Typography align="center" sx={{ my: 2 }}>
          {t('manageEstateAd.steps.estateLocation.addOnMap')}
        </Typography>

        <MapComponent
          control={control}
          getValues={getValues}
          setValue={setValue}
        />

        <Divider sx={{ mt: 4 }} />

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ my: 2, cursor: 'pointer' }}
          onClick={() => setShowAddressFields((prev) => !prev)}
        >
          <Typography align="center">
            {t('manageEstateAd.steps.estateLocation.address')}
          </Typography>
          <ExpandMore
            fontSize="large"
            sx={{
              transform: showAddressFields ? 'rotate(180deg)' : 'none',
              transition: (theme) => theme.transitions.create(['transform']),
            }}
          />
        </Stack>

        <Collapse in={showAddressFields}>
          {fields.map((field, index) => (
            <Grid container spacing={2} key={field.formId}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`location.addresses.${index}.city` as const}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={
                        t('manageEstateAd.steps.estateLocation.city')
                        // '*'
                      }
                      error={!!error}
                      helperText={error?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`location.addresses.${index}.area` as const}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={
                        t('manageEstateAd.steps.estateLocation.area')
                        // '*'
                      }
                      error={!!error}
                      helperText={error?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`location.addresses.${index}.street` as const}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={
                        t('manageEstateAd.steps.estateLocation.street')
                        // '*'
                      }
                      error={!!error}
                      helperText={error?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`location.addresses.${index}.streetNumber` as const}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={
                        t('manageEstateAd.steps.estateLocation.streetNumber')
                        // '*'
                      }
                      error={!!error}
                      helperText={error?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`location.addresses.${index}.postalCode` as const}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={
                        t('manageEstateAd.steps.estateLocation.postalCode')
                        // '*'
                      }
                      error={!!error}
                      helperText={error?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
            </Grid>
          ))}
        </Collapse>

        {/* to be included as feature in the future */}
        {/* <Box>
          <Controller
            name="location.isPublic"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    onChange={(e) => field.onChange(e.target.checked)}
                    checked={field.value}
                  />
                }
                label={
                  t(
                    'manageEstateAd.steps.estateLocation.showLocationOnWebsite'
                  ) as string
                }
                sx={{ my: 2 }}
              />
            )}
          />
        </Box> */}
      </Box>
    </>
  )
}

export default StepPropertyLocation
